import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import Axios from 'axios';
import liff from '@line/liff';
import { GET_SYSTEM_LIFF_PROFILE_API } from '@Apis/apiPath';
import CodePack from '@Apis/statusCode';

/**
 * @typedef {Object} LiffContextValue
 * @property {boolean} isReady        LIFF init 是否完成（完成後才可安全操作 URL 與呼叫 LIFF API）
 * @property {string}  initError      init / 取 profile 失敗訊息（空字串代表無錯誤）
 * @property {boolean} isInClient     是否於 LINE App 內開啟
 * @property {string}  liffId         後端下發的 LIFF ID
 * @property {string}  officeAccountUrl     官方帳號加好友連結
 * @property {string}  officeAccountChatUrl 官方帳號聊天連結（解除封鎖用）
 */

/** @type {React.Context<LiffContextValue>} */
const LiffContext = createContext({
    isReady: false,
    initError: '',
    isInClient: false,
    liffId: '',
    officeAccountUrl: '',
    officeAccountChatUrl: '',
});

/**
 * 全站根層 LIFF Provider：整個 MemberSite 只執行一次 liff.init，透過 Context 下傳給子元件。
 * 依 LIFF 官方建議，init 應收斂在根元件執行一次；若各子元件各自 init，SDK v2.27.2 起會印出警告。
 *
 * - liffId 來自後端非同步 API，故需先取得 profile 才能 init。
 * - useRef 防止 React 18 StrictMode 下重複 init。
 * - isReady 需等 init 完成才為 true，供 App.js 的 liff.state 還原 gate 使用；init 前不可操作 URL。
 * - 刻意不用 useAxios：本呼叫是 app 尚未起來前的根層 bootstrap，不該觸發 useAxios 的頁面級副作用
 *   （loading 轉圈、401 導 /login 等）；且此 Provider 位於 SnackbarProvider 外層，物理上也用不了。
 */
const LiffProvider = ({ children }) => {
    const [isReady, setIsReady] = useState(false);
    const [initError, setInitError] = useState('');
    const [profile, setProfile] = useState({
        liffId: '',
        officeAccountUrl: '',
        officeAccountChatUrl: '',
    });

    const initializedRef = useRef(false);

    useEffect(() => {
        // StrictMode double-invoke 守衛：真正只 init 一次
        if (initializedRef.current) return;
        initializedRef.current = true;

        const initLiff = async () => {
            try {
                const respBody = await Axios.get(GET_SYSTEM_LIFF_PROFILE_API);
                const resource = respBody && respBody.data;

                if (!resource || !resource.success || resource.statusCode !== CodePack.STATUS_SUCCESS_CODE || !resource.result) {
                    throw new Error('取得 LIFF profile 失敗');
                }

                const result = resource.result;
                setProfile({
                    liffId: result.liffId || '',
                    officeAccountUrl: result.officeAccountUrl || '',
                    officeAccountChatUrl: result.officeAccountChatUrl || '',
                });

                /**
                 * 只做 init，不在此觸發登入：若讓 init 順便自動觸發外部登入，會連只做 token 交接的
                 * /auth 頁也被拖去外部登入導轉——導轉時 redirect_uri 會落在 /auth、遺失原本的 ?t=，
                 * 且會跟 AuthPage 自身的導頁邏輯產生競態而卡死。故 in-client init 完成即視為已登入；
                 * 外部瀏覽器未登入時，改由 /login 的 LiffLoginPage 明確觸發 liff.login。
                 */
                await liff.init({ liffId: result.liffId });

                setIsReady(true);
            } catch (err) {
                // v2.23.2+ 可讀 LiffError.cause 供診斷；不靜默，向下暴露錯誤供子元件呈現
                console.error('LIFF 初始化錯誤', err, err && err.cause);
                setInitError('登入初始化失敗，請重新整理或稍後再試');
            }
        };

        initLiff();
        // eslint-disable-next-line
    }, []);

    const value = {
        isReady,
        initError,
        // init 完成後 liff.isInClient() 才可靠；未完成前一律回 false
        isInClient: isReady ? liff.isInClient() : false,
        liffId: profile.liffId,
        officeAccountUrl: profile.officeAccountUrl,
        officeAccountChatUrl: profile.officeAccountChatUrl,
    };

    return <LiffContext.Provider value={value}>{children}</LiffContext.Provider>;
};

/**
 * 取用 LIFF context。
 * @returns {LiffContextValue}
 */
export const useLiff = () => useContext(LiffContext);

export default LiffProvider;
